import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { updateDoc, doc } from 'firebase/firestore';
import { useDashboardDataContext } from '../contexts/DashboardDataContext';
import { db } from '../firebase';
import { Case, CaseStatus } from '../types';
import { t } from '../utils/translations';
import { getStatusFilterFromUrl } from '../components/Cases/casesUtils';
import { loadLatestNotesByCaseIds } from '../components/Cases/meetingNotesUtils';

export function useCasesData() {
  const {
    cases: cachedCases,
    sessionReportCounts,
    loading: dashboardLoading,
    upsertCase,
    incrementSessionReportCount,
  } = useDashboardDataContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cases, setCases] = useState<Case[]>([]);
  const [filteredCases, setFilteredCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all'>(() =>
    getStatusFilterFromUrl(searchParams)
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [editData, setEditData] = useState({ description: '' });

  const [meetingNotesOpen, setMeetingNotesOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [sessionReportOpen, setSessionReportOpen] = useState(false);
  const [selectedCaseForNotes, setSelectedCaseForNotes] = useState<Case | null>(null);
  const [caseNotes, setCaseNotes] = useState<Record<string, string>>({});
  const [caseTabloulSessions, setCaseTabloulSessions] = useState<Record<string, Set<number>>>(
    {}
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedCaseForDescription, setSelectedCaseForDescription] = useState<Case | null>(null);

  const caseIdFilter = searchParams.get('caseId');
  const caseIdsFilter = useMemo(
    () =>
      searchParams
        .get('caseIds')
        ?.split(',')
        .map((id) => id.trim())
        .filter(Boolean) ?? [],
    [searchParams]
  );
  const focusFilter = searchParams.get('focus');

  // Reuse dashboard cache — no second full cases scan
  useEffect(() => {
    if (dashboardLoading) {
      setLoading(true);
      return;
    }
    setCases(cachedCases);
    setFilteredCases(cachedCases);
    setLoading(false);

    const ids = cachedCases.map((c) => c.id);
    void loadLatestNotesByCaseIds(ids)
      .then(({ latestContent, tabloulSessionsByCaseId }) => {
        setCaseNotes(latestContent);
        setCaseTabloulSessions(tabloulSessionsByCaseId);
      })
      .catch((err) => console.error('Error loading latest notes:', err));
  }, [dashboardLoading, cachedCases]);

  const handleStatusFilterChange = useCallback(
    (status: CaseStatus | 'all') => {
      setStatusFilter(status);
      setSearchParams({ status }, { replace: true });
    },
    [setSearchParams]
  );

  const clearCaseIdFilter = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('caseId');
    nextParams.delete('caseIds');
    nextParams.delete('focus');
    nextParams.delete('openNotes');
    nextParams.delete('addNote');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setStatusFilter(getStatusFilterFromUrl(searchParams));
  }, [searchParams]);

  useEffect(() => {
    if (loading) return;

    const targetId = searchParams.get('caseId');
    if (!targetId) return;

    const openNotes = searchParams.get('openNotes') === 'true';
    const addNote = searchParams.get('addNote') === 'true';
    if (!openNotes && !addNote) return;

    const caseItem = cases.find((c) => c.id === targetId);
    if (caseItem) {
      setSelectedCaseForNotes(caseItem);
      if (addNote) {
        setAddNoteOpen(true);
        setMeetingNotesOpen(false);
      } else {
        setMeetingNotesOpen(true);
        setAddNoteOpen(false);
      }
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('openNotes');
    nextParams.delete('addNote');
    setSearchParams(nextParams, { replace: true });
  }, [loading, cases, searchParams, setSearchParams]);

  useEffect(() => {
    let filtered = cases;

    if (caseIdFilter) {
      filtered = filtered.filter((caseItem) => caseItem.id === caseIdFilter);
    } else if (caseIdsFilter.length > 0) {
      const idSet = new Set(caseIdsFilter);
      filtered = filtered.filter((caseItem) => idSet.has(caseItem.id));
    } else {
      if (searchTerm) {
        filtered = filtered.filter(
          (caseItem) =>
            caseItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            caseItem.counseledName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            caseItem.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (statusFilter !== 'all') {
        filtered = filtered.filter((caseItem) => caseItem.status === statusFilter);
      }
    }

    setFilteredCases(filtered);
  }, [cases, searchTerm, statusFilter, caseIdFilter, caseIdsFilter]);

  const focusFilterLabel =
    focusFilter === 'reportsNeeded'
      ? 'Rapoarte necesare'
      : focusFilter === 'consentMissing'
        ? 'Fără consimțământ'
        : focusFilter === 'frequencyOverdue'
          ? 'Frecvență depășită'
          : null;

  const handleEditCase = useCallback((caseItem: Case) => {
    setSelectedCase(caseItem);
    setEditData({ description: caseItem.description || '' });
    setEditDialogOpen(true);
  }, []);

  const handleSaveCase = useCallback(async () => {
    if (!selectedCase || saveLoading) return;

    try {
      setSaveLoading(true);
      const description = editData.description.trim();
      const caseRef = doc(db, 'cases', selectedCase.id);
      await updateDoc(caseRef, {
        description,
        updatedAt: new Date(),
      });

      const updated: Case = {
        ...selectedCase,
        description,
        updatedAt: new Date(),
      };

      setCases((prevCases) =>
        prevCases.map((caseItem) => (caseItem.id === selectedCase.id ? updated : caseItem))
      );

      upsertCase(updated);

      setEditDialogOpen(false);
      setSnackbar({
        open: true,
        message: t.cases.updateSuccess || 'Caz actualizat cu succes',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating case:', err);
      setSnackbar({
        open: true,
        message: t.cases.updateError || 'Eroare la actualizarea cazului',
        severity: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  }, [selectedCase, editData, upsertCase, saveLoading]);

  const handleCloseEditDialog = useCallback(() => {
    if (saveLoading) return;
    setEditDialogOpen(false);
    setSelectedCase(null);
    setEditData({ description: '' });
  }, [saveLoading]);

  const handleOpenMeetingNotes = useCallback((caseItem: Case) => {
    setSelectedCaseForNotes(caseItem);
    setMeetingNotesOpen(true);
  }, []);

  const handleOpenAddNote = useCallback((caseItem: Case) => {
    setSelectedCaseForNotes(caseItem);
    setAddNoteOpen(true);
  }, []);

  const handleOpenSessionReport = useCallback((caseItem: Case) => {
    setSelectedCaseForNotes(caseItem);
    setSessionReportOpen(true);
  }, []);

  const handleNoteAdded = useCallback(async () => {
    if (cases.length === 0) return;
    try {
      const { latestContent, tabloulSessionsByCaseId } = await loadLatestNotesByCaseIds(
        cases.map((c) => c.id)
      );
      setCaseNotes(latestContent);
      setCaseTabloulSessions(tabloulSessionsByCaseId);
    } catch (err) {
      console.error('Error refreshing notes:', err);
    }
  }, [cases]);

  const handleSessionReportSaved = useCallback(async () => {
    if (selectedCaseForNotes?.id) {
      incrementSessionReportCount(selectedCaseForNotes.id);
    }
    await handleNoteAdded();
    setSessionReportOpen(false);
    setSelectedCaseForNotes(null);
  }, [selectedCaseForNotes, incrementSessionReportCount, handleNoteAdded]);

  const handleCaseClosedFromReport = useCallback(
    (updatedCase: Case) => {
      setCases((current) =>
        current.map((caseItem) =>
          caseItem.id === updatedCase.id ? updatedCase : caseItem
        )
      );
      upsertCase(updatedCase);
      setSessionReportOpen(false);
      setSelectedCaseForNotes(null);
    },
    [upsertCase]
  );

  const handleCloseMeetingNotes = useCallback(() => {
    setMeetingNotesOpen(false);
    if (!addNoteOpen) {
      setSelectedCaseForNotes(null);
    }
  }, [addNoteOpen]);

  const handleCloseAddNote = useCallback(() => {
    setAddNoteOpen(false);
    if (!meetingNotesOpen) {
      setSelectedCaseForNotes(null);
    }
  }, [meetingNotesOpen]);

  const handleCloseSessionReport = useCallback(() => {
    setSessionReportOpen(false);
    setSelectedCaseForNotes(null);
  }, []);

  const handleOpenDescription = useCallback((caseItem: Case) => {
    setSelectedCaseForDescription(caseItem);
    setDescriptionModalOpen(true);
  }, []);

  const activeCasesCount = cases.filter((c) => c.status === 'active').length;
  const waitingCasesCount = cases.filter((c) => c.status === 'waiting').length;

  return {
    cases,
    filteredCases,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    caseIdFilter,
    caseIdsFilter,
    focusFilterLabel,
    caseNotes,
    caseTabloulSessions,
    caseReportsCount: sessionReportCounts,
    activeCasesCount,
    waitingCasesCount,
    editDialogOpen,
    saveLoading,
    selectedCase,
    editData,
    setEditData,
    meetingNotesOpen,
    addNoteOpen,
    sessionReportOpen,
    selectedCaseForNotes,
    descriptionModalOpen,
    setDescriptionModalOpen,
    selectedCaseForDescription,
    snackbar,
    setSnackbar,
    handleStatusFilterChange,
    clearCaseIdFilter,
    handleEditCase,
    handleSaveCase,
    handleCloseEditDialog,
    handleOpenMeetingNotes,
    handleOpenAddNote,
    handleOpenSessionReport,
    handleNoteAdded,
    handleSessionReportSaved,
    handleCaseClosedFromReport,
    handleCloseMeetingNotes,
    handleCloseAddNote,
    handleCloseSessionReport,
    handleOpenDescription,
  };
}
